import { useQuery } from "@apollo/client";
import { IonPage, IonSplitPane } from "@ionic/react";
import { useContext, useEffect } from "react";
import { Redirect, Route, Switch } from "react-router";

import EverpresentTags from "@components/EverpresentTags";
import EverpresentTasks from "@components/EverpresentTasks";
import ExpertFabs from "@components/ExpertFabs";
import ExpertJourneyActivitiesPage from "@components/expertJourney/ExpertJourneyActivitiesPage";
import ExpertJourneyArticlePage from "@components/expertJourney/ExpertJourneyArticlePage";
import ExpertJourneyArticlesPage from "@components/expertJourney/ExpertJourneyArticlesPage";
import ExpertJourneyCancelConsultPage from "@components/expertJourney/ExpertJourneyCancelConsultPage";
import ExpertJourneyCarePlanPage from "@components/expertJourney/ExpertJourneyCarePlanPage";
import ExpertJourneyCareProfilePage from "@components/expertJourney/ExpertJourneyCareProfilePage";
import ExpertJourneyClientAccountPage from "@components/expertJourney/ExpertJourneyClientAccountPage";
import ExpertJourneyClientVisibleTaskPage from "@components/expertJourney/ExpertJourneyClientVisibleTaskPage";
import ExpertJourneyCommunityPage from "@components/expertJourney/ExpertJourneyCommunityPage";
import ExpertJourneyDrivePage from "@components/expertJourney/ExpertJourneyDrivePage";
import ExpertJourneyEmployerPage from "@components/expertJourney/ExpertJourneyEmployerPage";
import ExpertJourneyEventsPage from "@components/expertJourney/ExpertJourneyEventsPage";
import ExpertJourneyExpertInfoPage from "@components/expertJourney/ExpertJourneyExpertInfoPage";
import ExpertJourneyExpertPage from "@components/expertJourney/ExpertJourneyExpertPage";
import ExpertJourneyExplorePage from "@components/expertJourney/ExpertJourneyExplorePage";
import ExpertJourneyFolderPage from "@components/expertJourney/ExpertJourneyFolderPage";
import ExpertJourneyGoalPage from "@components/expertJourney/ExpertJourneyGoalPage";
import ExpertJourneyGoalTemplatesTablePage from "@components/expertJourney/ExpertJourneyGoalsTablePage";
import ExpertJourneyGoalTemplateCreatePage from "@components/expertJourney/ExpertJourneyGoalTemplateCreatePage";
import ExpertJourneyGoalTemplateEditPage from "@components/expertJourney/ExpertJourneyGoalTemplateEditPage";
import ExpertJourneyGoalTemplatePage from "@components/expertJourney/ExpertJourneyGoalTemplatePage";
import ExpertJourneyGoalTemplatePreviewPage from "@components/expertJourney/ExpertJourneyGoalTemplatePreviewPage";
import ExpertJourneyGoalTemplatesPage from "@components/expertJourney/ExpertJourneyGoalTemplatesPage";
import ExpertJourneyJoinConsultPage from "@components/expertJourney/ExpertJourneyJoinConsultPage";
import ExpertJourneyMarketplaceItemPage from "@components/expertJourney/ExpertJourneyMarketplaceItemPage";
import ExpertJourneyMarketplacePage from "@components/expertJourney/ExpertJourneyMarketplacePage";
import ExpertJourneyMenu from "@components/expertJourney/ExpertJourneyMenu";
import ExpertJourneyMessagesPage from "@components/expertJourney/ExpertJourneyMessagesPage";
import styles from "@components/expertJourney/ExpertJourneyRouter.module.css";
import ExpertJourneyScheduleSessionPage from "@components/expertJourney/ExpertJourneyScheduleSessionPage";
import ExpertJourneyTimeLogsPage from "@components/expertJourney/ExpertJourneyTimeLogsPage";
import ExpertJourneyWorksheetPage from "@components/expertJourney/ExpertJourneyWorksheetPage";
import ExpertJourneyWorksheetTemplatePage from "@components/expertJourney/ExpertJourneyWorksheetTemplatePage";
import ExpertJourneyBannerProvider from "@components/ExpertJourneyBannerProvider";
import HandleQuery from "@components/HandleQuery";
import MessagesSmall from "@components/messaging/MessagesSmall";
import NavigatingJourneyPage from "@components/NavigatingJourneyPage";
import PageNotFoundWithMenuPage from "@components/PageNotFoundWithMenuPage";
import TimeLogModal from "@components/TimeLogModal";
import { openerContext } from "@context/Contexts";
import useSubscribeToMessages from "@hooks/subscriptions/useSubscribeToMessages";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  GetJourneyForMessagesDocument,
  GetJourneyForMessagesQuery,
  GetJourneyForMessagesQueryVariables,
  JourneyOpenDocument,
  JourneyOpenMutation,
  JourneyOpenMutationVariables
} from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  journeyId: string;
};

const ExpertJourneyRouter = ({ journeyId }: Props) => {
  const myExpert = useMyExpert();
  const { toggleOpener } = useContext(openerContext);

  useSubscribeToMessages();

  const [journeyOpen] = useMutationWithValidation<JourneyOpenMutation, JourneyOpenMutationVariables>(
    JourneyOpenDocument,
    "journey"
  );

  const getJourneyForMessagesQuery = useQuery<GetJourneyForMessagesQuery, GetJourneyForMessagesQueryVariables>(
    GetJourneyForMessagesDocument,
    {
      variables: {
        id: journeyId
      }
    }
  );

  useEffect(() => {
    journeyOpen({ id: journeyId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyOpen, journeyId]);

  useEffect(() => {
    Object.values(OpenerTypeEnum).forEach(type => {
      if (location.pathname === journeyRoute({ journeyId, linkAfterJourney: `/${type}`, myExpert })) {
        toggleOpener(type, true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // These are old routes that redirect to current routes. This is mainly needed for old lifecycle links.
  const redirectRouteMap = [
    { from: "/medication", to: "/care-profile" },
    { from: "/legal", to: "/care-profile" },
    { from: "/provider", to: "/care-profile" },
    { from: "/personalize", to: "/care-plan" },
    { from: "/learn", to: "/explore" },
    { from: "/care-profile/basic", to: "/care-profile" },
    { from: "/care-profile/medical", to: "/care-profile" },
    { from: "/care-profile/functional", to: "/care-profile" },
    { from: "/care-profile/legal", to: "/care-profile" },
    { from: "/care-profile/financial", to: "/care-profile" },
    { from: "/care-profile/social", to: "/care-profile" },
    { from: "/care-profile/well-being", to: "/care-profile" },
    { from: "/consults", to: "/expert" },
    { from: "/documents", to: "/drive" },
    { from: "/home", to: "/care-plan" },
    { from: "/safety", to: "/care-profile" },
    { from: "/internal-notes", to: "/care-plan" }
  ];

  if (!myExpert) {
    sessionStorage.setItem("postLoginRedirectTo", `${location.pathname}${location.hash}${location.search}`);
    return <Redirect to="/login" />;
  }

  return (
    <ExpertJourneyBannerProvider journeyId={journeyId}>
      <IonSplitPane className={styles.splitPane} contentId="main">
        <ExpertJourneyMenu journeyId={journeyId} />
        <IonPage id="main">
          <Switch>
            <Route
              exact
              path="/expert/journeys/:journeyId/care-journey"
              render={params => (
                <Redirect
                  to={journeyRoute({
                    journeyId: params.match.params.journeyId,
                    linkAfterJourney: "/care-plan",
                    myExpert
                  })}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-plan"
              render={params => <ExpertJourneyCarePlanPage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/client-visible-tasks/:taskId"
              render={params => (
                <ExpertJourneyClientVisibleTaskPage
                  journeyId={params.match.params.journeyId}
                  taskId={params.match.params.taskId}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/activity"
              render={params => <ExpertJourneyActivitiesPage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/articles/:articleId"
              render={params => (
                <ExpertJourneyArticlePage
                  articleId={params.match.params.articleId}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/articles"
              render={params => <ExpertJourneyArticlesPage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/drive/folders/:folderId"
              render={params => (
                <ExpertJourneyFolderPage
                  folderId={params.match.params.folderId}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />

            <Route
              exact
              path="/expert/journeys/:journeyId/community"
              render={params => <ExpertJourneyCommunityPage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/drive"
              render={params => <ExpertJourneyDrivePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/explore/events"
              render={params => <ExpertJourneyEventsPage journeyId={params.match.params.journeyId} />}
            />

            <Route
              exact
              path="/expert/journeys/:journeyId/explore"
              render={params => <ExpertJourneyExplorePage journeyId={params.match.params.journeyId} />}
            />

            <Route
              exact
              path="/expert/journeys/:journeyId/care-plan/journey"
              render={() => <NavigatingJourneyPage />}
            />

            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/schedule-consult"
              render={params => <ExpertJourneyScheduleSessionPage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile/basic"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile/medical"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile/functional"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile/legal"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile/financial"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile/next-consult"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile/social"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile/stage"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-profile/well-being"
              render={params => <ExpertJourneyCareProfilePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/employers/:employerId"
              render={params => (
                <ExpertJourneyEmployerPage
                  employerId={params.match.params.employerId}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/explore/marketplace"
              render={params => <ExpertJourneyMarketplacePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/consults/:consultId/cancel"
              render={params => (
                <ExpertJourneyCancelConsultPage
                  consultId={params.match.params.consultId}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/consults/:consultId/join"
              render={params => (
                <ExpertJourneyJoinConsultPage
                  consultId={params.match.params.consultId}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/expert-info/:expertId"
              render={params => (
                <ExpertJourneyExpertInfoPage
                  expertId={params.match.params.expertId}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/employer-benefits"
              render={params => <ExpertJourneyMarketplacePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/employer-benefits/:id"
              render={params => (
                <ExpertJourneyMarketplaceItemPage
                  employerBenefitId={params.match.params.id}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/worksheets/:id"
              render={params => (
                <ExpertJourneyWorksheetPage
                  journeyId={params.match.params.journeyId}
                  worksheetId={params.match.params.id}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/worksheet-templates/:id"
              render={params => (
                <ExpertJourneyWorksheetTemplatePage
                  journeyId={params.match.params.journeyId}
                  worksheetTemplateId={params.match.params.id}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-plan/goals/:id"
              render={params => (
                <ExpertJourneyGoalPage goalId={params.match.params.id} journeyId={params.match.params.journeyId} />
              )}
            />

            <Route
              exact
              path="/expert/journeys/:journeyId/care-plan/goal-templates"
              render={params => <ExpertJourneyGoalTemplatesPage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/goal-templates/table"
              render={params => <ExpertJourneyGoalTemplatesTablePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/goal-templates/new"
              render={params => <ExpertJourneyGoalTemplateCreatePage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/care-plan/goal-templates/:id"
              render={params => (
                <ExpertJourneyGoalTemplatePage
                  goalTemplateId={params.match.params.id}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/goal-templates/:id/preview"
              render={params => (
                <ExpertJourneyGoalTemplatePreviewPage
                  goalTemplateId={params.match.params.id}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId/goal-templates/:id/edit"
              render={params => (
                <ExpertJourneyGoalTemplateEditPage
                  goalTemplateId={params.match.params.id}
                  journeyId={params.match.params.journeyId}
                />
              )}
            />

            <Route
              exact
              path="/expert/journeys/:journeyId/time-logs"
              render={params => <ExpertJourneyTimeLogsPage journeyId={params.match.params.journeyId} />}
            />
            <Route
              exact
              path="/expert/journeys/:journeyId"
              render={data => {
                type ParamsWithJourneyId = { journeyId: string };
                const params = data.match.params as ParamsWithJourneyId;
                return (
                  <Redirect
                    to={journeyRoute({ journeyId: params.journeyId, linkAfterJourney: `/care-plan`, myExpert })}
                  />
                );
              }}
            />

            <Route
              exact
              path="/expert/journeys/:journeyId/expert"
              render={params => <ExpertJourneyExpertPage journeyId={params.match.params.journeyId} />}
            />

            <Route
              exact
              path="/expert/journeys/:journeyId/account"
              render={params => <ExpertJourneyClientAccountPage journeyId={params.match.params.journeyId} />}
            />

            <Route
              exact
              path="/expert/journeys/:journeyId/messages"
              render={params => <ExpertJourneyMessagesPage journeyId={params.match.params.journeyId} />}
            />

            {redirectRouteMap.map(({ from, to }) => (
              <Redirect
                from={`/expert/journeys/:journeyId${from}`}
                key={from}
                to={`/expert/journeys/:journeyId${to}`}
              />
            ))}

            <Route render={() => <PageNotFoundWithMenuPage journeyId={journeyId} />} />
          </Switch>
        </IonPage>
      </IonSplitPane>
      <ExpertFabs journeyId={journeyId} />
      <EverpresentTags journeyId={journeyId} />
      <EverpresentTasks journeyId={journeyId} />
      <HandleQuery hideLoader query={getJourneyForMessagesQuery}>
        {journeyData => (
          <MessagesSmall
            isFab
            journey={journeyData.journey}
            resourceData={undefined}
            setResourceData={() => {}}
            setWorksheetData={() => {}}
            worksheetData={undefined}
          />
        )}
      </HandleQuery>

      <TimeLogModal journeyId={journeyId} />
    </ExpertJourneyBannerProvider>
  );
};

export default ExpertJourneyRouter;
